import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  History as HistoryIcon, 
  Restore as RestoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import scheduleOfActivitiesService from '../../services/scheduleOfActivities.service';

const VersionHistoryDialog = ({ open, onClose, protocolId, onRollback, allowRollback = true }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rollingBack, setRollingBack] = useState(null);

  useEffect(() => {
    if (open && protocolId) {
      fetchVersionHistory();
    }
  }, [open, protocolId]);

  const fetchVersionHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await scheduleOfActivitiesService.getVersionHistory(protocolId);
      if (response.success) {
        setVersions(response.data);
      } else {
        setError('Failed to fetch version history');
      }
    } catch (error) {
      console.error('Error fetching version history:', error);
      setError('Error fetching version history: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (versionNumber) => {
    if (!allowRollback) return;
    setRollingBack(versionNumber);
    try {
      await onRollback(versionNumber);
      onClose();
    } catch (error) {
      console.error('Error rolling back:', error);
      setError('Error rolling back: ' + error.message);
    } finally {
      setRollingBack(null);
    }
  };

  const getChangeTypeColor = (changeType) => {
    switch (changeType) {
      case 'generated': return 'primary';
      case 'regenerated': return 'secondary';
      case 'edited': return 'success';
      case 'imported': return 'info';
      case 'rollback': return 'warning';
      default: return 'default';
    }
  };

  const getChangeTypeLabel = (changeType) => {
    switch (changeType) {
      case 'generated': return 'Generated';
      case 'regenerated': return 'Regenerated';
      case 'edited': return 'Edited';
      case 'imported': return 'Imported';
      case 'rollback': return 'Rollback';
      default: return changeType;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon />
          Version History
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : versions.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No version history found
          </Typography>
        ) : (
          <List>
            {versions.map((version) => (
              <ListItem key={version._id} divider>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Version {version.versionNumber}
                      </Typography>
                      {version.isLatestVersion && (
                        <Chip label="Current" color="primary" size="small" />
                      )}
                      {version.changeHistory && version.changeHistory.length > 0 && (
                        <Chip 
                          label={getChangeTypeLabel(version.changeHistory[0].changeType)}
                          color={getChangeTypeColor(version.changeHistory[0].changeType)}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {new Date(version.createdAt).toLocaleString()}
                      </Typography>
                      {version.changeHistory && version.changeHistory.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {version.changeHistory[0].changes}
                        </Typography>
                      )}
                      {version.metadata && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Generated by: {version.metadata.generatedBy || 'Unknown'}
                          </Typography>
                          {version.metadata.currentPrompt && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Prompt: {version.metadata.currentPrompt.substring(0, 100)}
                              {version.metadata.currentPrompt.length > 100 ? '...' : ''}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {allowRollback && !version.isLatestVersion && (
                    <IconButton
                      edge="end"
                      onClick={() => handleRollback(version.versionNumber)}
                      disabled={rollingBack === version.versionNumber}
                      title="Rollback to this version"
                    >
                      {rollingBack === version.versionNumber ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RestoreIcon />
                      )}
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {error && (
          <Button onClick={fetchVersionHistory} color="primary">
            Retry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default VersionHistoryDialog;


